import { useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";

import { EmergencyContactReference } from "../components/EmergencyContactReference.js";
import { Icon } from "../components/Icon.js";
import type { IconName } from "../components/Icon.js";
import { LanguageSettings } from "../components/LanguageSettings.js";
import { BuildInformation } from "../components/BuildInformation.js";
import { PageIntro } from "../components/PageIntro.js";
import { UpdateManager } from "../components/UpdateManager.js";
import { useI18n } from "../i18n/I18nProvider.js";
import { learnResearchMetadata } from "../learn/researchMetadata.js";
import { supplementalDraftMetadata } from "../learn/supplementalDraftMetadata.js";
import { practicalLearnContent, practicalSectionLabels } from "../learn/practicalContent.js";
import { learnLifeContexts } from "../learn/lifeContexts.js";
import { learnCategories, type LearnCategory } from "../learn/contentRepository.js";
import { useLearnContentRecords } from "../learn/offlineBundle.js";
import { FamilyPreparednessPlanPage } from "../family/FamilyPlanPage.js";
import { DisasterMode } from "../disaster/DisasterMode.js";
import type { TranslationKey } from "../i18n/translations.js";

const learningTopics = [
  {
    slug: "everyday",
    icon: "home",
    image: "/images/learning-accident-prevention-v1.png",
    title: "learn.everyday.title",
    detail: "learn.everyday.detail",
  },
  {
    slug: "accidents",
    icon: "alert",
    image: "/images/learning-accidents-v1.png",
    title: "learn.accidents.title",
    detail: "learn.accidents.detail",
  },
  {
    slug: "first-aid",
    icon: "check",
    image: "/images/learning-first-aid-v1.png",
    title: "learn.first_aid.title",
    detail: "learn.first_aid.detail",
  },
  {
    slug: "practical",
    icon: "book",
    image: "/images/learning-practical-tips-v1.png",
    title: "learn.practical.title",
    detail: "learn.practical.detail",
  },
  {
    slug: "outdoors",
    icon: "shield",
    image: "/images/learning-survival-v1.png",
    title: "learn.outdoors.title",
    detail: "learn.outdoors.detail",
  },
  {
    slug: "disasters",
    icon: "search",
    image: "/images/learning-disasters-v1.png",
    title: "learn.disasters.title",
    detail: "learn.disasters.detail",
  },
  {
    slug: "chemicals",
    icon: "alert",
    image: "/images/learning-chemicals-v1.png",
    title: "learn.chemicals.title",
    detail: "learn.chemicals.detail",
  },
  {
    slug: "medicines",
    icon: "check",
    image: "/images/learning-medicines-v1.png",
    title: "learn.medicines.title",
    detail: "learn.medicines.detail",
  },
  {
    slug: "food",
    icon: "book",
    image: "/images/learning-food-safety-v1.png",
    title: "learn.food.title",
    detail: "learn.food.detail",
  },
] as const;

const disasterTopics = [
  {
    icon: "alert",
    title: "disaster.topic.earthquake",
    detail: "disaster.topic.earthquake_detail",
    path: "/learn/earthquake",
    status: "research_only",
  },
  {
    icon: "shield",
    title: "disaster.topic.flood",
    detail: "disaster.topic.flood_detail",
    path: "/learn/flood-storm",
    status: "review_required",
  },
  {
    icon: "search",
    title: "disaster.topic.coast",
    detail: "disaster.topic.coast_detail",
    path: "/learn/tsunami",
    status: "review_required",
  },
  {
    icon: "home",
    title: "disaster.topic.fire",
    detail: "disaster.topic.fire_detail",
    path: "/learn/fire-evacuation",
    status: "research_only",
  },
] as const;

const homeUtilities = [
  {
    path: "/contacts",
    icon: "book",
    title: "directory.short_title",
    body: "home.area.contacts",
  },
  { path: "/search", icon: "search", title: "nav.search", body: "home.area.search" },
  { path: "/family-plan", icon: "family", title: "nav.family", body: "home.area.family" },
  {
    path: "/readiness",
    icon: "check",
    title: "home.area.readiness.title",
    body: "home.area.readiness.body",
  },
] as const;

const chemicalSubtopics = [
  "learn.chemicals.labels",
  "learn.chemicals.storage",
  "learn.chemicals.combinations",
  "learn.chemicals.exposure",
] as const;

const medicineSubtopics = [
  "learn.medicines.labels",
  "learn.medicines.medicine_medicine",
  "learn.medicines.medicine_food",
  "learn.medicines.professional",
] as const;

const foodSubtopics = [
  "learn.food.labels",
  "learn.food.allergens",
  "learn.food.storage",
  "learn.food.interactions",
] as const;

const outdoorEnvironments = [
  { slug: "forest", title: "learn.outdoors.forest", detail: "learn.outdoors.forest_detail" },
  { slug: "sea", title: "learn.outdoors.sea", detail: "learn.outdoors.sea_detail" },
  { slug: "cold", title: "learn.outdoors.cold", detail: "learn.outdoors.cold_detail" },
] as const;

const floodSituationResources = [
  {
    icon: "water",
    url: "https://bigdata-swoc.rid.go.th/pier/all",
    label: {
      th: "ระดับน้ำ — กรมชลประทาน",
      en: "Water levels — RID",
    },
    note: {
      th: "สถานีวัดน้ำและอัตราการไหล",
      en: "Stations and flow data",
    },
    official: true,
  },
  {
    icon: "map",
    url: "https://telemetry.dwr.go.th/home?layerMapType=BASIN",
    label: {
      th: "แผนที่ลุ่มน้ำ — กรมทรัพยากรน้ำ",
      en: "Basin map — DWR",
    },
    note: {
      th: "ค้นหาตามลุ่มน้ำและพื้นที่",
      en: "Search by basin and area",
    },
    official: true,
  },
  {
    icon: "map",
    url: "https://world.tehx.dyndns.info/flood#tab=map",
    label: {
      th: "แผนที่น้ำท่วม — World Tehx",
      en: "Flood map — World Tehx",
    },
    note: {
      th: "แหล่งภายนอก: รอตรวจสอบ",
      en: "External source: under review",
    },
    official: false,
  },
  {
    icon: "camera",
    url: "https://cctv.maholan.net/",
    label: {
      th: "กล้องถนน — Maholan",
      en: "Road cameras — Maholan",
    },
    note: {
      th: "แหล่งภายนอก: รอตรวจสอบ",
      en: "External source: under review",
    },
    official: false,
  },
] as const;

const learnCardIcons: Readonly<Record<string, IconName>> = {
  earthquake: "alert",
  tsunami: "water",
  "flood-storm": "water",
  "fire-evacuation": "alert",
  chemicals: "alert",
  "first-aid": "check",
  "public-safety": "shield",
  "family-preparedness": "family",
  medicines: "check",
  "food-labels": "book",
};

// Cards are an entry point, not a table of contents. Keep their language short
// enough to scan on a phone; the complete title and guidance remain on the detail page.
const learnCardCopy = {
  earthquake: {
    th: { title: "แผ่นดินไหว", detail: "หมอบ กำบัง ยึด" },
    en: { title: "Earthquake", detail: "Drop, cover, hold on" },
  },
  tsunami: {
    th: { title: "สึนามิ", detail: "เห็นสัญญาณ รีบขึ้นที่สูง" },
    en: { title: "Tsunami", detail: "See a warning? Move uphill" },
  },
  "flood-storm": {
    th: { title: "น้ำท่วมและพายุ", detail: "เช็กเตือนภัย เลี่ยงน้ำ" },
    en: { title: "Floods & storms", detail: "Check alerts. Avoid water" },
  },
  "fire-evacuation": {
    th: { title: "หนีไฟ", detail: "ออกทางบันได ไม่ย้อนกลับ" },
    en: { title: "Fire escape", detail: "Use stairs. Stay out" },
  },
  chemicals: {
    th: { title: "สารเคมี", detail: "อ่านฉลาก ไม่ผสมสาร" },
    en: { title: "Chemicals", detail: "Read labels. Never mix" },
  },
  "first-aid": {
    th: { title: "ปฐมพยาบาล", detail: "ดูความปลอดภัย เรียกช่วย" },
    en: { title: "First aid", detail: "Check safety. Get help" },
  },
  "public-safety": {
    th: { title: "ปลอดภัยในที่สาธารณะ", detail: "ออกห่างหรือหลบให้ปลอดภัย" },
    en: { title: "Public safety", detail: "Leave danger or shelter" },
  },
  "family-preparedness": {
    th: { title: "ความขัดแย้งรุนแรงหรือสงคราม", detail: "เตรียมครอบครัว ติดต่อ และสิ่งจำเป็น" },
    en: {
      title: "Armed conflict or war",
      detail: "Prepare contacts, meeting points, and essentials",
    },
  },
  medicines: {
    th: { title: "ใช้ยาให้ปลอดภัย", detail: "เช็กชื่อยาและฉลาก" },
    en: { title: "Medicine safety", detail: "Check medicine and label" },
  },
  "food-labels": {
    th: { title: "อ่านฉลากอาหาร", detail: "เช็กส่วนประกอบและวันหมดอายุ" },
    en: { title: "Food labels", detail: "Check ingredients and expiry" },
  },
} as const;

const learnEditorialVisuals = {
  earthquake: {
    src: "/images/learn-earthquake-editorial-v1.png",
    alt: {
      th: "ภาพประกอบการเตรียมพร้อมรับแผ่นดินไหวในบ้าน",
      en: "Editorial illustration of earthquake readiness at home",
    },
  },
  tsunami: {
    src: "/images/learn-tsunami-editorial-v1.png",
    alt: {
      th: "ภาพประกอบการอพยพขึ้นที่สูงจากชายฝั่ง",
      en: "Editorial illustration of moving uphill from the coast",
    },
  },
  "flood-storm": {
    src: "/images/learn-flood-storm-editorial-v1.png",
    alt: {
      th: "ภาพประกอบการเตรียมรับน้ำท่วมและพายุ",
      en: "Editorial illustration of flood and storm readiness",
    },
  },
  "fire-evacuation": {
    src: "/images/learn-fire-evacuation-editorial-v1.png",
    alt: {
      th: "ภาพประกอบการใช้บันไดเพื่อออกจากอาคาร",
      en: "Editorial illustration of using stairs to leave a building",
    },
  },
  chemicals: {
    src: "/images/learn-chemicals-editorial-v1.png",
    alt: {
      th: "ภาพประกอบการใช้สารเคมีในบ้านอย่างปลอดภัย",
      en: "Editorial illustration of safer household chemical use",
    },
  },
  "first-aid": {
    src: "/images/learn-first-aid-editorial-v1.png",
    alt: {
      th: "ภาพประกอบชุดปฐมพยาบาลและการขอความช่วยเหลืออย่างปลอดภัย",
      en: "Editorial illustration of a first-aid kit and safe help-seeking",
    },
  },
  "public-safety": {
    src: "/images/learn-public-safety-editorial-v1.png",
    alt: {
      th: "ภาพประกอบการสังเกตทางออกในที่สาธารณะ",
      en: "Editorial illustration of noticing safer exits in public",
    },
  },
  "family-preparedness": {
    src: "/images/learn-family-preparedness-editorial-v1.png",
    alt: {
      th: "ภาพประกอบการจัดแผนและของจำเป็นของครอบครัว",
      en: "Editorial illustration of organising a family plan and essentials",
    },
  },
  medicines: {
    src: "/images/learn-medicines-editorial-v1.png",
    alt: {
      th: "ภาพประกอบการอ่านฉลากยาอย่างรอบคอบ",
      en: "Editorial illustration of carefully checking a medicine label",
    },
  },
  "food-labels": {
    src: "/images/learn-food-labels-editorial-v1.png",
    alt: {
      th: "ภาพประกอบการอ่านฉลากอาหารและส่วนประกอบ",
      en: "Editorial illustration of checking food labels and ingredients",
    },
  },
} as const;

function EnvironmentMark({ kind }: { readonly kind: "forest" | "sea" | "cold" }) {
  return (
    <svg
      aria-hidden="true"
      className="environment-mark"
      viewBox="0 0 80 80"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {kind === "forest" && (
        <>
          <path d="M40 9 17 49h46L40 9Z M40 28 22 61h36L40 28Z M40 61v11" />
        </>
      )}
      {kind === "sea" && (
        <>
          <path d="M5 36c9 0 9-8 18-8s9 8 18 8 9-8 18-8 9 8 16 8M5 52c9 0 9-8 18-8s9 8 18 8 9-8 18-8 9 8 16 8" />
        </>
      )}
      {kind === "cold" && (
        <>
          <path d="M40 8v64M12 24l56 32M12 56l56-32M33 15l7 7 7-7M33 65l7-7 7 7M14 34l10-2-3-10M59 58l-3-10 10-2M21 58l3-10-10-2M66 34l-10-2 3-10" />
        </>
      )}
    </svg>
  );
}

function LearningOutline({
  title,
  items,
  note,
}: {
  readonly title: TranslationKey;
  readonly items: readonly TranslationKey[];
  readonly note: TranslationKey;
}) {
  const { t } = useI18n();
  return (
    <section aria-labelledby="learning-outline-title" className="learning-topic__outline">
      <h2 id="learning-outline-title">{t(title)}</h2>
      <ul>
        {items.map((item) => (
          <li key={item}>{t(item)}</li>
        ))}
      </ul>
      <p>{t(note)}</p>
    </section>
  );
}

export function LearningPage() {
  const { locale, t } = useI18n();
  const contentRecords = useLearnContentRecords();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedCategory = searchParams.get("category");
  const selectedCategory = learnCategories.some((category) => category.id === requestedCategory)
    ? (requestedCategory as LearnCategory)
    : null;
  const [query, setQuery] = useState("");
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const shownTopics = contentRecords.filter((record) => {
    const topic = record.metadata;
    const matchesCategory = selectedCategory === null || record.category === selectedCategory;
    const searchable =
      `${t(topic.title)} ${t(topic.detail)} ${t(topic.source)} ${record.practical[locale].serviceDisruption?.intro ?? ""} ${record.practical[locale].serviceDisruption?.actions.map((action) => `${action.title} ${action.text}`).join(" ") ?? ""}`.toLocaleLowerCase();
    return matchesCategory && (!normalizedQuery || searchable.includes(normalizedQuery));
  });
  return (
    <div className="page-stack page-stack--home learning-page">
      <header className="home-intro">
        <div className="home-intro__meta">
          <span>{t("learn.eyebrow")}</span>
          <span>SAFESPHERE / 002</span>
        </div>
        <h1>{t("learn.title")}</h1>
        <p>{t("learn.body")}</p>
      </header>
      <section
        aria-labelledby="learn-library-title"
        className="learn-library-intro route-state route-state--notice"
      >
        <p className="eyebrow">{t("learn.library.kicker")}</p>
        <h2 id="learn-library-title">{t("learn.library.title")}</h2>
        <p>{t("learn.library.boundary")}</p>
        <p className="learn-library-intro__diagnostic" role="status">
          {t("learn.library.diagnostic_count")}
        </p>
        <Link className="learn-library-intro__contacts" to="/contacts">
          <Icon name="book" />
          {t("learn.library.contacts")}
        </Link>
      </section>
      <section aria-labelledby="learn-category-title" className="learn-category-browser">
        <div className="learn-category-browser__heading">
          <h2 id="learn-category-title">
            {locale === "th" ? "เลือกหมวดเพื่อกรองหัวข้อ" : "Filter by category"}
          </h2>
          <p>
            {locale === "th"
              ? "ทั้ง 10 หัวข้อแสดงอยู่ด้านล่าง เลือกหมวดเพื่อดูเฉพาะเรื่องที่เกี่ยวข้อง"
              : "All 10 topics are listed below. Select a category to narrow the list."}
          </p>
        </div>
        <div className="learn-category-browser__grid">
          {learnCategories.map((category) => {
            const copy = category[locale];
            const count = contentRecords.filter((record) => record.category === category.id).length;
            return (
              <button
                aria-pressed={selectedCategory === category.id}
                className="learn-category-card"
                key={category.id}
                onClick={() => setSearchParams({ category: category.id })}
                type="button"
              >
                <img alt="" src={category.image} />
                <span className="learn-category-card__body">
                  <strong>{copy.title}</strong>
                  <small>{copy.description}</small>
                  <span>
                    {count} {locale === "th" ? "หัวข้อ" : count === 1 ? "topic" : "topics"}
                  </span>
                  <b>{locale === "th" ? "กรองหมวดนี้ →" : "Filter this category →"}</b>
                </span>
              </button>
            );
          })}
        </div>
      </section>
      <section aria-label={t("learn.library.search_label")} className="learn-library-filters">
        <label className="learn-library-search">
          <span>{t("learn.library.search_label")}</span>
          <input
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("learn.library.search_placeholder")}
            type="search"
            value={query}
          />
        </label>
        {selectedCategory && (
          <button className="learn-library-clear" onClick={() => setSearchParams({})} type="button">
            {locale === "th" ? "แสดงหมวดหมู่ทั้งหมด" : "Show all categories"}
          </button>
        )}
      </section>
      <section
        aria-label={t("learn.topics_label")}
        className="home-categories learn-library"
        id="learn-topics"
      >
        <div className="home-categories__grid">
          {shownTopics.map(({ metadata: topic, category }, index) => {
            const cardCopy = learnCardCopy[topic.slug as keyof typeof learnCardCopy]?.[locale];

            return (
              <Link
                className="home-category learning-index-card learn-library-card"
                key={topic.slug}
                to={
                  topic.slug === "family-preparedness"
                    ? "/learn/family-preparedness#learn-service-disruption-title"
                    : `/learn/${topic.slug}`
                }
              >
                <span className="home-category__number" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="learn-library-card__icon" aria-hidden="true">
                  <Icon name={learnCardIcons[topic.slug] ?? "book"} />
                </span>
                <span className="home-category__text">
                  <strong>{cardCopy?.title ?? t(topic.title)}</strong>
                  <small>{cardCopy?.detail ?? t(topic.detail)}</small>
                </span>
                <span className="learn-library-card__meta">
                  <span>
                    {learnCategories.find((item) => item.id === category)?.[locale].title}
                  </span>
                  <span>{t("learn.library.section_count")}</span>
                </span>
                <span className={`learn-status learn-status--${topic.status}`}>
                  {t(`learn.library.status.${topic.status}`)}
                </span>
                <span aria-hidden="true" className="home-category__arrow">
                  ↗
                </span>
              </Link>
            );
          })}
        </div>
        {shownTopics.length === 0 && (
          <div className="route-state route-state--unavailable">
            <h2>{t("learn.library.empty.title")}</h2>
            <p>{t("learn.library.empty.body")}</p>
          </div>
        )}
      </section>
    </div>
  );
}

export function LearningTopicPage() {
  const { locale, t } = useI18n();
  const contentRecords = useLearnContentRecords();
  const { topic: slug } = useParams();
  const canonicalSlug =
    slug === "medication-labels"
      ? "medicines"
      : slug === "food-allergies" || slug === "food"
        ? "food-labels"
        : null;
  if (canonicalSlug) {
    return <Navigate replace to={`/learn/${canonicalSlug}`} />;
  }
  const canonicalRecord = contentRecords.find((record) => record.metadata.slug === slug);
  const libraryTopic = [...learnResearchMetadata, ...supplementalDraftMetadata].find(
    (item) => item.slug === slug,
  );
  const topic = learningTopics.find((item) => item.slug === slug);
  if (libraryTopic) {
    const practical =
      canonicalRecord?.practical[locale] ?? practicalLearnContent[libraryTopic.slug]?.[locale];
    const labels = practicalSectionLabels[locale];
    const everydaySituations = practical
      ? [...practical.situations, ...(learnLifeContexts[libraryTopic.slug]?.[locale] ?? [])]
      : [];
    const editorialVisual =
      learnEditorialVisuals[libraryTopic.slug as keyof typeof learnEditorialVisuals];
    const practicalSections = practical
      ? [
          { icon: "family" as const, title: labels.prepare, items: practical.prepare },
          { icon: "alert" as const, title: labels.avoid, items: practical.avoid },
          { icon: "shield" as const, title: labels.help, items: practical.help },
        ]
      : [];
    return (
      <div className="page-stack learning-topic">
        <Link className="learning-topic__back" to="/learn">
          ← {t("learn.back")}
        </Link>
        <header
          className={`learning-topic__header${editorialVisual ? " learning-topic__header--visual" : ""}`}
        >
          {editorialVisual && (
            <img
              alt=""
              aria-hidden="true"
              className="learning-topic__hero-image"
              src={editorialVisual.src}
            />
          )}
          <div className="learning-topic__header-content">
            <p className="eyebrow">{t("learn.library.kicker")}</p>
            <h1>{t(libraryTopic.title)}</h1>
            <p>{t(libraryTopic.detail)}</p>
          </div>
        </header>
        <section
          aria-label={t("learn.library.status_label")}
          className="learning-topic__notice"
          role="status"
        >
          <strong className={`learn-status learn-status--${libraryTopic.status}`}>
            {t(`learn.library.status.${libraryTopic.status}`)}
          </strong>
          <p>{t("learn.library.detail_boundary")}</p>
        </section>
        <main aria-labelledby="learn-explanation-title" className="learn-practical-guide">
          <section className="learn-practical-guide__lead">
            <p className="learn-detail-summary__label">{labels.know}</p>
            <h2 id="learn-explanation-title">{labels.what}</h2>
            <p>{t(libraryTopic.explanation)}</p>
            <p>
              <strong>{t("learn.detail.why")}</strong> {t(libraryTopic.whyItMatters)}
            </p>
            {practical && (
              <details className="learn-practical-section learn-practical-guide__observations">
                <summary>
                  <Icon name="alert" />
                  {labels.notice}
                </summary>
                <ul className="learn-practical-guide__key-points">
                  {practical.notice.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </details>
            )}
          </section>
          {practical && (
            <section
              aria-label={locale === "th" ? "สามอย่างจำง่าย" : "Three things to remember"}
              className="learn-practical-guide__quick"
            >
              <article>
                <Icon name="alert" />
                <div>
                  <strong>{locale === "th" ? "ดู" : "Look"}</strong>
                  <p>{practical.notice[0]}</p>
                </div>
              </article>
              <article>
                <Icon name="family" />
                <div>
                  <strong>{locale === "th" ? "เตรียม" : "Get ready"}</strong>
                  <p>{practical.prepare[0]}</p>
                </div>
              </article>
              <article>
                <Icon name="check" />
                <div>
                  <strong>{locale === "th" ? "ทำ" : "Act"}</strong>
                  <p>{practical.during[0]}</p>
                </div>
              </article>
            </section>
          )}
          {practical && (
            <details className="learn-practical-section learn-practical-section--now">
              <summary>
                <Icon name="check" />
                {labels.now}
              </summary>
              <ul>
                {practical.during.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </details>
          )}
          {practical?.emergencyKit && (
            <section
              aria-labelledby="learn-emergency-kit-title"
              className="learn-practical-guide__emergency-kit"
            >
              <h2 className="learn-section-heading" id="learn-emergency-kit-title">
                <Icon name="family" />
                {labels.emergencyKit}
              </h2>
              <p className="learn-practical-guide__emergency-kit-intro">
                {practical.emergencyKit.intro}
              </p>
              <p className="learn-practical-guide__emergency-kit-target">
                <Icon name="check" />
                {practical.emergencyKit.target}
              </p>
              <div className="learn-practical-guide__emergency-kit-grid">
                {practical.emergencyKit.items.map((item) => (
                  <article key={item.title}>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </article>
                ))}
              </div>
              <p className="learn-practical-guide__emergency-kit-rotation">
                <Icon name="check" />
                {practical.emergencyKit.rotation}
              </p>
            </section>
          )}
          {practical?.serviceDisruption && (
            <section
              aria-labelledby="learn-service-disruption-title"
              className="learn-practical-guide__service-disruption"
            >
              <h2 className="learn-section-heading" id="learn-service-disruption-title">
                <Icon name="phone" />
                {labels.serviceDisruption}
              </h2>
              <p className="learn-practical-guide__service-disruption-intro">
                {practical.serviceDisruption.intro}
              </p>
              <div className="learn-practical-guide__service-disruption-grid">
                {practical.serviceDisruption.actions.map((action) => (
                  <article key={action.title}>
                    <Icon name="check" />
                    <div>
                      <h3>{action.title}</h3>
                      <p>{action.text}</p>
                    </div>
                  </article>
                ))}
              </div>
              <p className="learn-practical-guide__service-disruption-boundary">
                <Icon name="alert" />
                {practical.serviceDisruption.boundary}
              </p>
            </section>
          )}
          {practical?.cautions && practical.cautions.length > 0 && (
            <section
              aria-labelledby="learn-food-medicine-checks"
              className="learn-practical-guide__cautions"
            >
              <h2 className="learn-section-heading" id="learn-food-medicine-checks">
                <Icon name="alert" />
                {labels.cautions}
              </h2>
              <p>
                {locale === "th"
                  ? "เป็นจุดให้เช็กก่อนตัดสินใจ ไม่ใช่รายการสั่งห้ามสำหรับทุกคน"
                  : "These are checks before deciding, not a universal list of forbidden foods."}
              </p>
              <div>
                {practical.cautions.map((caution) => (
                  <article key={caution.title}>
                    <h3>{caution.title}</h3>
                    <p>{caution.text}</p>
                  </article>
                ))}
              </div>
            </section>
          )}
          <div className="learn-practical-guide__sections">
            {practicalSections.map((section) => (
              <details className="learn-practical-section" key={section.title}>
                <summary>
                  <Icon name={section.icon} />
                  {section.title}
                </summary>
                <ul>
                  {section.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
          {practical && (
            <>
              <section className="learn-practical-guide__example">
                <h2 className="learn-section-heading">
                  <Icon name="alert" />
                  {labels.example}
                </h2>
                <p>{practical.example}</p>
              </section>
              <details className="learn-practical-section learn-practical-guide__situations">
                <summary>
                  <Icon name="home" />
                  {labels.situations} ({everydaySituations.length})
                </summary>
                <div>
                  {everydaySituations.map((situation) => (
                    <article key={situation.title}>
                      <h3>{situation.title}</h3>
                      <p>{situation.text}</p>
                    </article>
                  ))}
                </div>
              </details>
              <section className="learn-practical-guide__checklist">
                <h2 className="learn-section-heading">
                  <Icon name="check" />
                  {labels.checklist}
                </h2>
                <ul>
                  {practical.checklist.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
              <section className="learn-source-shortcuts">
                <h2 className="learn-section-heading">
                  <Icon name="book" />
                  {locale === "th" ? "อ่านต่อจากแหล่งข้อมูล" : "Explore source links"}
                </h2>
                <p>
                  {locale === "th"
                    ? "เปิดข้อมูลต้นทางเพื่ออ่านรายละเอียดล่าสุด"
                    : "Open the original source for fuller, current details."}
                </p>
                <div>
                  {libraryTopic.sources
                    .slice(
                      0,
                      libraryTopic.slug === "medicines" ||
                        libraryTopic.slug === "food-labels" ||
                        libraryTopic.slug === "family-preparedness"
                        ? 4
                        : 2,
                    )
                    .map((source) => (
                      <a href={source.url} key={source.url} rel="noreferrer" target="_blank">
                        <Icon name="book" />
                        <span>{source.label}</span>
                        <span aria-hidden="true">↗</span>
                      </a>
                    ))}
                </div>
              </section>
              {libraryTopic.slug === "flood-storm" && (
                <section aria-labelledby="flood-situation-title" className="flood-situation-links">
                  <p className="learn-detail-summary__label">
                    {locale === "th" ? "ข้อมูลเพิ่มเติม" : "Additional information"}
                  </p>
                  <h2 id="flood-situation-title">
                    {locale === "th"
                      ? "ติดตามสถานการณ์น้ำในพื้นที่"
                      : "Check water conditions in your area"}
                  </h2>
                  <p>
                    {locale === "th"
                      ? "เปิดดูข้อมูลล่าสุดจากเว็บไซต์ต้นทาง และยึดประกาศของหน่วยงานในพื้นที่เมื่อเกิดเหตุจริง"
                      : "Check the originating site for the latest information and follow local authorities during an incident."}
                  </p>
                  <Link className="flood-situation-links__contact" to="/contacts?q=1784">
                    <Icon name="phone" />
                    <span>
                      <strong>
                        {locale === "th"
                          ? "แจ้งเหตุภัยพิบัติ — ปภ. 1784"
                          : "Report a disaster — DDPM 1784"}
                      </strong>
                      <small>
                        {locale === "th"
                          ? "ดูขอบเขตบริการก่อนโทร"
                          : "Check the service scope first."}
                      </small>
                      <span className="is-external">
                        {locale === "th" ? "REVIEW REQUIRED →" : "REVIEW REQUIRED →"}
                      </span>
                    </span>
                  </Link>
                  <ul>
                    {floodSituationResources.map((resource) => (
                      <li key={resource.url}>
                        <a href={resource.url} rel="noreferrer" target="_blank">
                          <Icon name={resource.icon} />
                          <span>
                            <strong>{resource.label[locale]}</strong>
                            <small>{resource.note[locale]}</small>
                            <span className={resource.official ? "is-official" : "is-external"}>
                              {resource.official
                                ? locale === "th"
                                  ? "ทางการ ↗"
                                  : "Official ↗"
                                : locale === "th"
                                  ? "ภายนอก / รอตรวจ ↗"
                                  : "External / review ↗"}
                            </span>
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                  <p className="flood-situation-links__boundary">
                    {locale === "th"
                      ? "SafeSphere ไม่ดึงหรือรับรองข้อมูลสดจากลิงก์เหล่านี้ และไม่ฝังภาพกล้องในแอป"
                      : "SafeSphere does not ingest or verify live data from these links, and does not embed camera feeds."}
                  </p>
                </section>
              )}
              {canonicalRecord && (
                <section aria-labelledby="learn-related-title" className="learn-related">
                  <h2 id="learn-related-title">
                    {locale === "th" ? "หัวข้อที่เกี่ยวข้อง" : "Related topics"}
                  </h2>
                  <div>
                    {canonicalRecord.relatedSlugs.map((relatedSlug) => {
                      const related = contentRecords.find(
                        (record) => record.metadata.slug === relatedSlug,
                      );
                      if (!related) return null;
                      return (
                        <Link key={relatedSlug} to={`/learn/${relatedSlug}`}>
                          {t(related.metadata.title)} <span aria-hidden="true">→</span>
                        </Link>
                      );
                    })}
                  </div>
                </section>
              )}
            </>
          )}
        </main>
        {libraryTopic.slug === "outdoors" && (
          <section aria-labelledby="outdoor-environments-title" className="outdoor-environments">
            <h2 id="outdoor-environments-title">{t("learn.outdoors.environments")}</h2>
            <div className="outdoor-environments__grid">
              {outdoorEnvironments.map((environment, index) => (
                <Link
                  className="outdoor-environment"
                  key={environment.slug}
                  to={`/learn/outdoors/${environment.slug}`}
                >
                  <span className="outdoor-environment__number" aria-hidden="true">
                    0{index + 1}
                  </span>
                  <EnvironmentMark kind={environment.slug} />
                  <strong>{t(environment.title)}</strong>
                  <small>{t(environment.detail)}</small>
                  <span className="outdoor-environment__status">
                    {t("learn.library.status.review_required")}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}
        <details className="learn-evidence" open>
          <summary>
            <Icon name="book" />
            {labels.evidence}
          </summary>
          <section
            aria-labelledby="learn-provenance-title"
            className="learning-topic__outline learn-provenance"
          >
            <h2 id="learn-provenance-title">{t("learn.library.provenance")}</h2>
            <dl>
              <div>
                <dt>{t("learn.detail.category")}</dt>
                <dd>{t(`learn.library.category.${libraryTopic.category}`)}</dd>
              </div>
              <div>
                <dt>{t("learn.detail.scope")}</dt>
                <dd>{t(libraryTopic.scope)}</dd>
              </div>
              <div>
                <dt>{t("learn.library.researched_label")}</dt>
                <dd>{libraryTopic.researchedOn}</dd>
              </div>
              <div>
                <dt>{t("learn.detail.review")}</dt>
                <dd>{t(`learn.library.status.${libraryTopic.status}`)}</dd>
              </div>
              <div>
                <dt>{t("learn.library.publication")}</dt>
                <dd>{t("learn.library.not_published")}</dd>
              </div>
            </dl>
          </section>
          <div className="learn-detail-support">
            <section aria-labelledby="learn-sources-title" className="learn-source-records">
              <h2 id="learn-sources-title">{t("learn.detail.sources")}</h2>
              <p>
                {t("learn.library.source")}: {t(libraryTopic.source)}
              </p>
              <p className="learn-source-records__note">{t("learn.detail.sources_note")}</p>
              <ul>
                {libraryTopic.sources.map((source) => {
                  const external = source.url.startsWith("http");
                  return (
                    <li key={source.url}>
                      <a
                        href={source.url}
                        rel={external ? "noreferrer" : undefined}
                        target={external ? "_blank" : undefined}
                      >
                        {source.label}
                        <span aria-hidden="true">{external ? "↗" : "→"}</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
            <section aria-labelledby="learn-gaps-title" className="learn-detail-limitations">
              <h2 id="learn-gaps-title">{t("learn.detail.gaps")}</h2>
              <p>{t(libraryTopic.gaps)}</p>
              <p className="learn-detail-limitations__boundary">
                {t("learn.detail.research_only")}
              </p>
            </section>
          </div>
        </details>
        <Link className="learn-library-intro__contacts" to="/contacts">
          <Icon name="book" />
          {t("learn.library.contacts")}
        </Link>
        <Link className="learning-topic__return" to="/learn">
          ↑ {t("learn.return_to_index")}
        </Link>
      </div>
    );
  }
  if (!topic) return <NotFoundPage />;
  return (
    <div className="page-stack learning-topic">
      <Link className="learning-topic__back" to="/learn">
        ← {t("learn.back")}
      </Link>
      <header className="learning-topic__header">
        <p className="eyebrow">{t("learn.eyebrow")}</p>
        <h1>{t(topic.title)}</h1>
        <p>{t(topic.detail)}</p>
      </header>
      {topic.slug !== "outdoors" && (
        <div className="learning-topic__notice" role="status">
          <strong>{t("learn.status")}</strong>
          <p>{t("learn.unavailable")}</p>
        </div>
      )}
      {topic.slug === "chemicals" && (
        <LearningOutline
          title="learn.chemicals.outline"
          items={chemicalSubtopics}
          note="learn.chemicals.unknown"
        />
      )}
      {topic.slug === "medicines" && (
        <LearningOutline
          title="learn.medicines.outline"
          items={medicineSubtopics}
          note="learn.medicines.unknown"
        />
      )}
      {topic.slug === "food" && (
        <LearningOutline
          title="learn.food.outline"
          items={foodSubtopics}
          note="learn.food.unknown"
        />
      )}
      {topic.slug === "outdoors" && (
        <section aria-labelledby="outdoor-environments-title" className="outdoor-environments">
          <h2 id="outdoor-environments-title">{t("learn.outdoors.environments")}</h2>
          <div className="outdoor-environments__grid">
            {outdoorEnvironments.map((environment, index) => (
              <Link
                className="outdoor-environment"
                key={environment.slug}
                to={`/learn/outdoors/${environment.slug}`}
              >
                <span className="outdoor-environment__number" aria-hidden="true">
                  0{index + 1}
                </span>
                <EnvironmentMark kind={environment.slug} />
                <strong>{t(environment.title)}</strong>
                <small>{t(environment.detail)}</small>
                <span className="outdoor-environment__status">{t("learn.status")}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
      {topic.slug === "outdoors" && (
        <p className="outdoor-environments__note">{t("learn.unavailable")}</p>
      )}
      <Link className="learning-topic__return" to="/learn">
        ↑ {t("learn.return_to_index")}
      </Link>
    </div>
  );
}

export function OutdoorEnvironmentPage() {
  const { environment: slug } = useParams();
  const environment = outdoorEnvironments.find((item) => item.slug === slug);
  if (!environment) return <NotFoundPage />;
  return <Navigate replace to={`/learn/outdoors-${environment.slug}`} />;
}

export function HomePage() {
  const { locale, t } = useI18n();

  const learnCategoryByHomeSlug: Readonly<
    Partial<Record<(typeof learningTopics)[number]["slug"], LearnCategory>>
  > = {
    everyday: "everyday-risks",
    accidents: "everyday-risks",
    practical: "survival",
    outdoors: "survival",
    disasters: "disasters",
  };
  const areas = learningTopics.map((topic) => {
    const category = learnCategoryByHomeSlug[topic.slug];
    const categoryCopy = learnCategories.find((item) => item.id === category)?.[locale];
    return {
      ...topic,
      title: categoryCopy?.title ?? t(topic.title),
      detail: categoryCopy?.description ?? t(topic.detail),
      path:
        category === undefined
          ? topic.slug === "food"
            ? "/learn/food-labels"
            : `/learn/${topic.slug}`
          : `/learn?category=${category}#learn-topics`,
      image: topic.image,
    };
  });
  const featuredAreas = ["everyday", "disasters", "outdoors"].map((slug) =>
    areas.find((area) => area.slug === slug)!,
  );
  const remainingAreas = areas.filter(
    (area) => !["everyday", "disasters", "outdoors"].includes(area.slug),
  );

  return (
    <div className="page-stack page-stack--home">
      <header className="home-intro">
        <div className="home-intro__meta">
          <span>{t("page.home.eyebrow")}</span>
          <span>SAFESPHERE / 001</span>
        </div>
        <h1 id="home-title">{t("page.home.title")}</h1>
        <p>{t("page.home.body")}</p>
      </header>
      <section aria-labelledby="home-paths-title" className="home-paths">
        <h2 className="visually-hidden" id="home-paths-title">
          {t("home.paths.title")}
        </h2>
        <Link className="home-path home-path--help" to="/emergency">
          <span className="home-path__top">
            <span>{t("home.path.help.label")}</span>
            <span aria-hidden="true">01 / 02</span>
          </span>
          <span className="home-path__content">
            <strong>{t("home.path.help.title")}</strong>
            <small>{t("home.path.help.body")}</small>
          </span>
          <span className="home-path__footer">
            <span>{t("emergency.action")}</span>
            <span aria-hidden="true">↗</span>
          </span>
        </Link>
        <Link className="home-path home-path--learn" to="/learn">
          <span className="home-path__top">
            <span>{t("home.path.learn.label")}</span>
            <span aria-hidden="true">02 / 02</span>
          </span>
          <span className="home-path__content">
            <strong>{t("home.path.learn.title")}</strong>
            <small>{t("home.path.learn.body")}</small>
          </span>
          <span className="home-path__footer">
            <span>{t("home.path.learn.action")}</span>
            <span aria-hidden="true">↗</span>
          </span>
        </Link>
      </section>
      <section className="featured-learning" aria-labelledby="featured-learning-title">
        <div className="featured-learning__heading">
          <div>
            <p className="eyebrow">{t("home.section.kicker")}</p>
            <h2 id="featured-learning-title">{t("page.home.tools")}</h2>
          </div>
          <span>03 / 09</span>
        </div>
        <div className="featured-learning__grid">
          {featuredAreas.map((area, index) => (
            <Link
              className={`featured-learning__tile featured-learning__tile--${area.slug}`}
              key={area.path}
              to={area.path}
            >
              <img alt="" className="home-learning-image" src={area.image} />
              <span className="featured-learning__number" aria-hidden="true">
                0{index + 1}
              </span>
              <span className="featured-learning__copy">
                <strong>{area.title}</strong>
                <small>{area.detail}</small>
              </span>
              <span className="featured-learning__action">
                {t("home.path.learn.action")} <b aria-hidden="true">↗</b>
              </span>
            </Link>
          ))}
        </div>
      </section>
      <section
        className="home-categories home-categories--compact"
        aria-labelledby="main-areas-title"
      >
        <div className="home-categories__heading">
          <div>
            <p className="eyebrow">{t("home.secondary.kicker")}</p>
            <h2 id="main-areas-title">{t("home.secondary.title")}</h2>
          </div>
          <span>06 / 09</span>
        </div>
        <div className="home-categories__grid">
          {remainingAreas.map((area, index) => (
            <Link
              className={`home-category${area.slug === "chemicals" ? " home-category--chemical" : ""}${area.slug === "medicines" || area.slug === "food" ? " home-category--health" : ""}`}
              key={area.path}
              to={area.path}
            >
              <img alt="" className="home-learning-image" src={area.image} />
              <span className="home-category__number" aria-hidden="true">
                0{index + 1}
              </span>
              <span className="home-category__text">
                <strong>{area.title}</strong>
                <small>{area.detail}</small>
              </span>
              <span className="home-category__status">
                {t("learn.library.status.research_only")}
              </span>
              <span className="home-category__icon" aria-hidden="true">
                <Icon name={area.icon} />
              </span>
              <span aria-hidden="true" className="home-category__arrow">
                ↗
              </span>
            </Link>
          ))}
        </div>
      </section>
      <section className="home-utilities" aria-labelledby="home-utilities-title">
        <div className="home-utilities__heading">
          <p className="eyebrow">{t("home.utilities.kicker")}</p>
          <h2 id="home-utilities-title">{t("home.utilities.title")}</h2>
        </div>
        <div className="home-utilities__grid">
          {homeUtilities.map((item, index) => (
            <Link className="home-utility" key={item.path} to={item.path}>
              <span className="home-utility__number" aria-hidden="true">
                0{index + 1}
              </span>
              <Icon name={item.icon} />
              <strong>{t(item.title)}</strong>
              <small>{t(item.body)}</small>
              <span className="home-utility__action">
                {t("home.utilities.action")} <b aria-hidden="true">↗</b>
              </span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

export function EmergencyPage() {
  return (
    <div className="page-stack page-stack--emergency">
      <DisasterMode />
      <EmergencyContactReference />
    </div>
  );
}

export function DisasterHubPage() {
  const { t } = useI18n();

  return (
    <div className="page-stack disaster-hub-page">
      <PageIntro
        body={t("page.disaster.body")}
        eyebrow={t("page.disaster.eyebrow")}
        title={t("page.disaster.title")}
      />
      <section className="product-draft-note" aria-labelledby="disaster-draft-title">
        <div>
          <span>{t("draft.status.RESEARCH_ONLY")}</span>
          <h2 id="disaster-draft-title">{t("disaster.draft.title")}</h2>
          <p>{t("disaster.draft.body")}</p>
        </div>
        <dl>
          <div>
            <dt>{t("draft.scope")}</dt>
            <dd>{t("disaster.draft.scope")}</dd>
          </div>
          <div>
            <dt>{t("draft.source")}</dt>
            <dd>
              <a
                href="https://www.disaster.go.th/upload/download/file_attach/58cb60f5607de.pdf"
                rel="noreferrer"
                target="_blank"
              >
                {t("disaster.draft.source")} ↗
              </a>
            </dd>
          </div>
          <div>
            <dt>{t("draft.researched")}</dt>
            <dd>2026-10-01</dd>
          </div>
          <div>
            <dt>{t("draft.limitations")}</dt>
            <dd>{t("disaster.draft.limitations")}</dd>
          </div>
        </dl>
      </section>
      <section aria-labelledby="disaster-topics-title" className="disaster-index">
        <div className="disaster-index__heading">
          <p className="eyebrow">{t("disaster.index.kicker")}</p>
          <h2 id="disaster-topics-title">{t("disaster.index.title")}</h2>
          <p>{t("disaster.index.body")}</p>
        </div>
        <div className="disaster-index__grid">
          {disasterTopics.map((topic, index) => (
            <Link className="disaster-index__item" key={topic.title} to={topic.path}>
              <span className="disaster-index__number" aria-hidden="true">
                0{index + 1}
              </span>
              <Icon name={topic.icon} />
              <h3>{t(topic.title)}</h3>
              <p>{t(topic.detail)}</p>
              <span className="disaster-index__status">
                {t(`learn.library.status.${topic.status}`)}
              </span>
              <b aria-hidden="true">↗</b>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

export function FamilyPlanPage() {
  return <FamilyPreparednessPlanPage />;
}

export function SettingsPage() {
  const { t } = useI18n();

  return (
    <div className="page-stack settings-page">
      <PageIntro
        body={t("page.settings.body")}
        eyebrow={t("page.settings.eyebrow")}
        title={t("page.settings.title")}
      />
      <BuildInformation />
      <LanguageSettings />
      <UpdateManager />
    </div>
  );
}

export function AboutPage() {
  const { t } = useI18n();

  return (
    <div className="page-stack about-page">
      <PageIntro body={t("about.body")} eyebrow={t("about.eyebrow")} title={t("about.title")} />
      <section aria-labelledby="about-purpose-title" className="about-purpose">
        <h2 id="about-purpose-title">{t("about.purpose_title")}</h2>
        <p>{t("about.purpose_body")}</p>
      </section>
      <section aria-labelledby="support-title" className="support-panel">
        <p className="support-panel__eyebrow">{t("support.eyebrow")}</p>
        <h2 id="support-title">{t("support.title")}</h2>
        <p>{t("support.body")}</p>
        <a
          className="support-panel__link"
          href="https://ko-fi.com/craftlabguru"
          rel="noreferrer"
          target="_blank"
        >
          {t("support.action")} <span aria-hidden="true">↗</span>
        </a>
      </section>
      <section aria-labelledby="about-books-title" className="about-books">
        <p>{t("about.books_eyebrow")}</p>
        <h2 id="about-books-title">{t("about.books_title")}</h2>
        <span>{t("about.books_body")}</span>
      </section>
    </div>
  );
}

export function NotFoundPage() {
  const { t } = useI18n();

  return (
    <div className="page-stack recovery-page">
      <PageIntro
        body={t("not_found.body")}
        eyebrow={t("placeholder.label")}
        title={t("not_found.title")}
      />
      <Link className="button button--primary" to="/">
        {t("not_found.action")}
      </Link>
    </div>
  );
}
